<template>
  <BMDialog
    :width="dialogWidth"
    :disableConfirm="disableConfirm"
    :showDialog="showData || showPermissions"
    :headerText="dialogTitle"
    :loading="isLoading"
    dialogTestId="new-user-dialog"
    @closed="closeDialog"
    @confirmed="handleConfirmed"
  >
    <v-row>
      <v-col v-if="showData">
        <UserDataDialog
          v-if="showUserData.user"
          :userDialog="showUserData"
          @userDataButtonDisabled="userDataButtonDisabled = $event"
          @userDataChanged="handleUserDataChange"
        />
      </v-col>
      <v-divider v-if="showData && showPermissions" vertical />
      <v-col v-if="showPermissions">
        <UserPermissionsDialog
          :permissionsDialog="showUserPermission"
          :selectedSubcontractor="selectedSubcontractor"
          @permissionChanged="handlePermissionChange"
          @permissionButtonDisabled="permissionButtonDisabled = $event"
        />
      </v-col>
    </v-row>
  </BMDialog>
</template>
<script>
import BMDialog from '@/components/common/utilities/BMDialog.vue';
import UserDataDialog from '@/components/common/user/UserDataDialog';
import UserPermissionsDialog from '@/components/common/user/UserPermissionsDialog';
import trackingEvents from '@/config/trackingEvents';
import { useUsersApi } from '@/composables/api/users/useUsersApi';
import { useNotification } from '@/composables/useNotification';

export default {
  name: 'NewUserDialog',
  components: {
    BMDialog,
    UserDataDialog,
    UserPermissionsDialog
  },
  props: {
    showUserData: {
      type: Object,
      required: false,
      default: () => ({ dialog: false, user: {} })
    },
    showUserPermission: {
      type: Object,
      required: false,
      default: () => ({ dialog: false, user: {} })
    },
    selectedSubcontractor: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  setup() {
    const { updateUserPermissions, editUser, addNewUser } = useUsersApi();
    const { showErrorAlert } = useNotification();

    return { updateUserPermissions, editUser, addNewUser, showErrorAlert };
  },
  data() {
    return {
      user: null,
      userDataButtonDisabled: true,
      permissionButtonDisabled: true,
      permissions: null,
      isLoading: false
    };
  },
  computed: {
    showData() {
      return !!this.showUserData.dialog;
    },
    showPermissions() {
      return !!this.showUserPermission.dialog;
    },
    disableConfirm() {
      if (this.isLoading) return true;
      return (
        (this.showData && this.userDataButtonDisabled) ||
        (this.showPermissions && this.permissionButtonDisabled)
      );
    },
    dialogTitle() {
      if (this.showData && this.showPermissions) {
        return this.$t('userManagement.createAccount');
      }
      if (this.showData) {
        const { firstName, lastName } = this.showUserData.user;
        return `${firstName} ${lastName}`;
      }
      if (this.showPermissions) {
        const { firstName, lastName } = this.showUserPermission.user;
        return `${firstName} ${lastName} ${this.$t('generic.permissions')}`;
      }
      return this.$t('userManagement.createAccount');
    },
    dialogWidth() {
      return this.showData && this.showPermissions ? '80%' : '50%';
    }
  },
  methods: {
    async addUser() {
      try {
        this.isLoading = true;
        await this.addNewUser({
          user: this.user,
          permissions: this.permissions.permissions
        });
        this.$emit('userAdded');
        trackingEvents.addUser();
        this.closeDialog();
      } catch (err) {
        this.showCodeBasedError(err);
      } finally {
        this.isLoading = false;
      }
    },
    async updateUser() {
      try {
        this.isLoading = true;
        await this.editUser({ ...this.user });
        this.$emit('userUpdated', this.user.id);
        this.closeDialog();
        trackingEvents.editUser();
      } catch (err) {
        this.showCodeBasedError(err);
      } finally {
        this.isLoading = false;
      }
    },
    async updatePermissions() {
      try {
        this.isLoading = true;
        await this.updateUserPermissions(this.permissions);
        this.$emit('userUpdated', this.permissions.id);
        this.closeDialog();
        trackingEvents.editPermissions();
      } catch {
        this.showErrorAlert();
      } finally {
        this.isLoading = false;
      }
    },
    showCodeBasedError(error) {
      this.showErrorAlert(
        'generic.error',
        this.$t(`userManagement.errorEnums.${error?.response?.data?.code}`, {
          email: this.user?.email,
          mobilePhone: this.user?.mobilePhone
        })
      );
    },
    handleConfirmed() {
      if (this.showData && this.showPermissions) {
        this.addUser();
      } else if (this.showData) {
        this.updateUser();
      } else if (this.showPermissions) {
        this.updatePermissions();
      }
    },
    closeDialog() {
      this.$emit('close');
    },
    handlePermissionChange(payload) {
      this.permissions = payload;
    },
    handleUserDataChange(payload) {
      this.user = payload;
    }
  }
};
</script>
